// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Utils
import { filterCriteria } from 'datastage/utils/filterCriteria';
// --- Config
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';

/**
 * Custom hook for Date Math Calculations grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Date Math Calculations grid
 * - Fetches sorted date math calculations data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Dependencies and handlers for the grid
 * @returns {object} { setSort, columns }
 */
export function useDateCalculationsGridUtils({
    datastageApi,
    filterForm,
    defaultFilterRange,
    updateDateMathCalculations,
    handlers,
}) {
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update date math calculations
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);
            handlers.setLoadingTrue();
            try {
                const data = await datastageApi.dateMathJobs(
                    filterCriteria(filterForm, '', 'dateMathCalculations'),
                    defaultFilterRange,
                    [colId, direction.toUpperCase()],
                );
                updateDateMathCalculations({
                    calculations: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    sortParam: [colId, direction.toUpperCase()],
                });
            } catch (error) {
                console.error('Error sorting date math calculations:', error);
            } finally {
                handlers.setLoadingFalse();
            }
        },
        [filterForm, defaultFilterRange, updateDateMathCalculations, handlers],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['dateMathCalculations'](...handlers.columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
